/**
 * Client for the workstation hardware service's optical document reader.
 *
 * The same shape as `domain/eidReader.ts` and for the same reason: the backend runs centrally, one
 * container for every shop, so it cannot reach a scanner plugged into a counter. The browser is the
 * transport, and everything a scan produces — the decoded document, the raw zone, the page images —
 * stays between this page and `127.0.0.1`.
 *
 * A scan is a **session** rather than a request: a document read takes as long as somebody takes to
 * get a card out of a wallet, and it has to be abandonable while it is happening. So starting one
 * and learning what it produced are separate calls, joined by an opaque session id that lives in
 * component memory and nowhere else.
 *
 * Pure apart from the injected `fetch`, so every branch is unit-testable.
 */

import {
    HARDWARE_SERVICE_ORIGIN,
    type FetchLike,
    type HardwareServiceStatus,
} from './labelPrinting';
import type { OpticalScanSession } from './opticalScan';

/** Capability id for scanning a document optically. */
export const CAPABILITY_DOCUMENT_SCAN = 'document.scan';

/** Capability id for the cropped portrait a scan produces. */
export const CAPABILITY_DOCUMENT_SCAN_PORTRAIT = 'document.scan.portrait';

/** Capability id for the visible-light page image. */
export const CAPABILITY_DOCUMENT_SCAN_VISIBLE = 'document.scan.visible';

/** Capability id for the infrared page image. */
export const CAPABILITY_DOCUMENT_SCAN_INFRARED = 'document.scan.infrared';

/** How long any single call to the service may take before the page stops waiting. */
export const DOCUMENT_REQUEST_TIMEOUT_MS = 15_000;

/**
 * How often a running scan is polled.
 *
 * The states a page shows change on the operator's own timescale — placing a document, the reader
 * pulling it in — so a second is responsive without asking a counter machine to do anything
 * noticeable. There is no realtime channel here on purpose: a scan is short, local and already
 * bounded by the service's own deadline.
 */
export const DOCUMENT_POLL_INTERVAL_MS = 1_000;

/**
 * Why a document operation did not do what was asked.
 *
 * The service's own codes, passed through rather than reinterpreted, plus the ones this client adds
 * for conditions the service never gets to report.
 */
export type DocumentScanErrorCode =
    /** Nothing answered on the loopback port, or this origin is not paired. */
    | 'SERVICE_UNAVAILABLE'
    /** This build of the service cannot scan documents on this platform. */
    | 'DOCUMENT_NOT_IMPLEMENTED'
    /** The call took longer than {@link DOCUMENT_REQUEST_TIMEOUT_MS}. */
    | 'REQUEST_TIMEOUT'
    /**
     * The caller abandoned the call - a closing modal, an unmounting page.
     *
     * Never rendered: whoever aborted has already moved on, and telling them their own cancellation
     * failed would be reporting their own action back to them as an error.
     */
    | 'REQUEST_ABORTED'
    /** The service answered with something this client does not recognise. */
    | 'UNEXPECTED_RESPONSE'
    | 'SDK_NOT_INSTALLED'
    | 'SDK_VERSION_UNSUPPORTED'
    | 'DOCUMENT_READER_UNAVAILABLE'
    | 'SELECTED_DEVICE_UNAVAILABLE'
    | 'SELECTED_DEVICE_UNKNOWN'
    | 'DEVICE_SELECTION_REQUIRED'
    | 'DEVICE_BUSY'
    | 'SCAN_LIMIT_REACHED'
    | 'SCAN_SESSION_UNKNOWN'
    | 'SCAN_SESSION_EXPIRED'
    | 'SCAN_NOT_COMPLETED'
    | 'SCAN_IMAGE_UNKNOWN'
    | 'SCAN_IMAGE_ALREADY_FETCHED'
    | 'SCAN_IMAGE_UNSUPPORTED'
    | 'INVALID_REQUEST'
    | 'INTERNAL_ERROR';

/** The codes the service is allowed to hand back, so an unknown one cannot masquerade as handled. */
const SERVICE_CODES: ReadonlySet<string> = new Set<DocumentScanErrorCode>([
    'DOCUMENT_NOT_IMPLEMENTED',
    'SDK_NOT_INSTALLED',
    'SDK_VERSION_UNSUPPORTED',
    'DOCUMENT_READER_UNAVAILABLE',
    'SELECTED_DEVICE_UNAVAILABLE',
    'SELECTED_DEVICE_UNKNOWN',
    'DEVICE_SELECTION_REQUIRED',
    'DEVICE_BUSY',
    'SCAN_LIMIT_REACHED',
    'SCAN_SESSION_UNKNOWN',
    'SCAN_SESSION_EXPIRED',
    'SCAN_NOT_COMPLETED',
    'SCAN_IMAGE_UNKNOWN',
    'SCAN_IMAGE_ALREADY_FETCHED',
    'SCAN_IMAGE_UNSUPPORTED',
    'INVALID_REQUEST',
    'INTERNAL_ERROR',
]);

/** One document reader, as the service describes it. */
export interface DetectedDocumentReader {
    id: string;
    provider: string;
    displayName: string;
    model: string | null;
    /** Four characters at most. The full serial never leaves the workstation service. */
    serialSuffix: string | null;
    transport: string;
    connected: boolean;
    evidence: string;
    selected: boolean;
    preferred: boolean;
    busy: boolean;
    statusReason: string | null;
}

/** What the vendor runtime on this workstation is. */
export type DocumentSdkState = 'approved' | 'not-installed' | 'unsupported' | 'unknown';

/** Body of `GET /v1/document/readers`. */
export interface DocumentReaderState {
    readers: DetectedDocumentReader[];
    /** The reader a scan carrying no device id would use. Null whenever that is ambiguous. */
    selectedDeviceId: string | null;
    /** The reader an operator chose, attached or not. A recommendation, never a substitute. */
    preferredDeviceId: string | null;
    selectionSource: string;
    selectionRequired: boolean;
    ready: boolean;
    sdk: DocumentSdkState;
    code: string | null;
    statusReason: string | null;
    observedAt: string;
}

/** Either what was asked for, or why not. */
export type DocumentResult<TValue> =
    | { ok: true; value: TValue }
    | { ok: false; code: DocumentScanErrorCode };

/** Whether a ready service advertises optical document scanning. */
export function canScanDocuments(status: HardwareServiceStatus): boolean {
    return (
        status.state === 'ready' && status.health.capabilities.includes(CAPABILITY_DOCUMENT_SCAN)
    );
}

/** The service's error body. */
interface DocumentErrorBody {
    error?: string;
    message?: string;
}

/** Turns whatever the service said into a code this page knows how to render. */
async function failure(response: Response): Promise<DocumentScanErrorCode> {
    if (response.status === 501) {
        return 'DOCUMENT_NOT_IMPLEMENTED';
    }

    let body: unknown;

    try {
        body = await response.json();
    } catch {
        return 'UNEXPECTED_RESPONSE';
    }

    const code = (body as DocumentErrorBody | null)?.error;

    // An unrecognised code is not passed through: a page that rendered a raw token would be showing
    // an operator something nobody wrote a sentence for.
    return typeof code === 'string' && SERVICE_CODES.has(code)
        ? (code as DocumentScanErrorCode)
        : 'UNEXPECTED_RESPONSE';
}

/**
 * One call to the service, bounded and abortable.
 *
 * The caller's signal and this call's own deadline are both honoured: a modal that closes must not
 * leave a request running, and a service that stops answering must not leave a page waiting.
 */
async function call<TValue>(
    fetchImpl: FetchLike,
    path: string,
    init: RequestInit,
    signal: AbortSignal | undefined,
    read: (response: Response) => Promise<TValue>,
): Promise<DocumentResult<TValue>> {
    const controller = new AbortController();
    let expired = false;
    const timeout = setTimeout(() => {
        expired = true;
        controller.abort();
    }, DOCUMENT_REQUEST_TIMEOUT_MS);
    const abort = () => controller.abort();

    signal?.addEventListener('abort', abort);

    const outcome = await deliver(
        () =>
            fetchImpl(`${HARDWARE_SERVICE_ORIGIN}${path}`, { ...init, signal: controller.signal }),
        read,
        () => reason(expired, signal),
    );

    // Cleanup after the await rather than in a `finally`, because `deliver` is written not to throw:
    // every way a call can end already comes back as a value.
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);

    return outcome;
}

/**
 * Performs one call and turns every way it can end into a value.
 *
 * The three failures are kept apart deliberately. Nothing answering is a transport problem; a
 * refusal is the service telling us something specific; and a 200 whose body is not what the
 * contract says is neither - it is this client and that service disagreeing, which is worth saying
 * rather than reporting as the workstation being unreachable.
 */
async function deliver<TValue>(
    send: () => Promise<Response>,
    read: (response: Response) => Promise<TValue>,
    unreachable: () => DocumentScanErrorCode,
): Promise<DocumentResult<TValue>> {
    let response: Response;

    try {
        response = await send();
    } catch {
        return { ok: false, code: unreachable() };
    }

    if (!response.ok) {
        return { ok: false, code: await failure(response) };
    }

    try {
        return { ok: true, value: await read(response) };
    } catch {
        return { ok: false, code: 'UNEXPECTED_RESPONSE' };
    }
}

/** Which of the three ways a call can end without an answer this one was. */
function reason(expired: boolean, signal: AbortSignal | undefined): DocumentScanErrorCode {
    if (expired) {
        return 'REQUEST_TIMEOUT';
    }

    return signal?.aborted === true ? 'REQUEST_ABORTED' : 'SERVICE_UNAVAILABLE';
}

/** Reads a JSON body, or reports that it was not one. */
async function json<TValue>(response: Response): Promise<TValue> {
    return (await response.json()) as TValue;
}

/** What document readers this workstation has, and whether one could scan now. */
export function listDocumentReaders(
    fetchImpl: FetchLike,
    signal?: AbortSignal,
): Promise<DocumentResult<DocumentReaderState>> {
    return call<DocumentReaderState>(
        fetchImpl,
        '/v1/document/readers',
        { method: 'GET' },
        signal,
        json,
    );
}

/**
 * Records which reader this workstation recommends, or clears the recommendation.
 *
 * Stored by the service, in its own protected state, beside the chip reader's. Never in the browser:
 * local storage is per-origin, per-profile and cleared by anyone who clears their browsing data,
 * which would silently return a two-reader counter to "choose again" in the middle of a shift.
 */
export function selectDocumentReader(
    fetchImpl: FetchLike,
    deviceId: string | null,
    signal?: AbortSignal,
): Promise<DocumentResult<DocumentReaderState>> {
    return call<DocumentReaderState>(
        fetchImpl,
        '/v1/document/readers/select',
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deviceId }),
        },
        signal,
        json,
    );
}

/**
 * Starts one scan on a named reader.
 *
 * The device id is always sent, even where the workstation has one reader: a scan that named nothing
 * would be relying on the service to resolve it, and the operator has already confirmed which reader
 * they are standing at.
 */
export function startDocumentScan(
    fetchImpl: FetchLike,
    deviceId: string | null,
    signal?: AbortSignal,
): Promise<DocumentResult<OpticalScanSession>> {
    return call<OpticalScanSession>(
        fetchImpl,
        '/v1/document/scans',
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deviceId }),
        },
        signal,
        json,
    );
}

/** Where one scan has got to, and what it read once it has finished. */
export function readDocumentScan(
    fetchImpl: FetchLike,
    sessionId: string,
    signal?: AbortSignal,
): Promise<DocumentResult<OpticalScanSession>> {
    return call<OpticalScanSession>(
        fetchImpl,
        `/v1/document/scans/${sessionId}`,
        { method: 'GET' },
        signal,
        json,
    );
}

/**
 * Asks a scan to stop. Idempotent.
 *
 * What the scan ends as is decided by what actually happened, not by this call: one that had already
 * read its document stays completed.
 */
export function cancelDocumentScan(
    fetchImpl: FetchLike,
    sessionId: string,
    signal?: AbortSignal,
): Promise<DocumentResult<OpticalScanSession>> {
    return call<OpticalScanSession>(
        fetchImpl,
        `/v1/document/scans/${sessionId}/cancel`,
        { method: 'POST' },
        signal,
        json,
    );
}

/**
 * Drops a scan's result and images. Idempotent.
 *
 * Called on every path that ends a modal — apply, cancel, close, retry, unmount — because the
 * service holding a person's document for five minutes after a page has finished with it is five
 * minutes longer than necessary.
 */
export function disposeDocumentScan(
    fetchImpl: FetchLike,
    sessionId: string,
    signal?: AbortSignal,
): Promise<DocumentResult<true>> {
    return call(fetchImpl, `/v1/document/scans/${sessionId}`, { method: 'DELETE' }, signal, () =>
        Promise.resolve(true as const),
    );
}

/**
 * Fetches one image, which consumes it.
 *
 * Single use, by the service's design: the bytes are handed over once and are then gone. The caller
 * owns what comes back and must revoke the object URL it makes from it.
 */
export function fetchDocumentScanImage(
    fetchImpl: FetchLike,
    sessionId: string,
    imageId: string,
    signal?: AbortSignal,
): Promise<DocumentResult<Blob>> {
    return call(
        fetchImpl,
        `/v1/document/scans/${sessionId}/images/${imageId}`,
        { method: 'GET' },
        signal,
        response => response.blob(),
    );
}

/**
 * Why a scan ended without a document.
 *
 * Distinct from {@link DocumentScanErrorCode}: these arrive in the `error` field of a perfectly
 * successful 200, because a scan that times out or is cancelled is a scan that ran, not a call that
 * failed. Each one has a sentence of its own, so an operator is told what to do rather than that
 * something went wrong.
 */
export type DocumentScanFailureCode =
    | 'DEVICE_LOST'
    | 'DOCUMENT_WAIT_TIMEOUT'
    | 'SCAN_CANCELLED'
    | 'WORKER_START_FAILED'
    | 'WORKER_PROTOCOL_ERROR'
    | 'WORKER_WATCHDOG_TERMINATED'
    | 'DOCUMENT_READ_FAILED'
    | 'CODELINE_DATA_INVALID'
    | 'IMAGE_UNAVAILABLE'
    | 'INTERNAL_ERROR';

const FAILURE_CODES: ReadonlySet<string> = new Set<DocumentScanFailureCode>([
    'DEVICE_LOST',
    'DOCUMENT_WAIT_TIMEOUT',
    'SCAN_CANCELLED',
    'WORKER_START_FAILED',
    'WORKER_PROTOCOL_ERROR',
    'WORKER_WATCHDOG_TERMINATED',
    'DOCUMENT_READ_FAILED',
    'CODELINE_DATA_INVALID',
    'IMAGE_UNAVAILABLE',
    'INTERNAL_ERROR',
]);

/**
 * The failure code a page has a sentence for, or `INTERNAL_ERROR` for one it does not.
 *
 * A code nobody wrote a translation for would render as a raw token on a counter screen, which tells
 * an operator nothing and looks like a bug even when the service behaved correctly.
 */
export function scanFailureCode(error: string | null): DocumentScanFailureCode | null {
    if (error === null) {
        return null;
    }

    return FAILURE_CODES.has(error) ? (error as DocumentScanFailureCode) : 'INTERNAL_ERROR';
}

/** Whether a scan has stopped, whatever it stopped as. */
export function isScanFinished(session: OpticalScanSession): boolean {
    return TERMINAL_STATES.has(session.state);
}

/** Whether a scan finished with a document to review. */
export function isScanSuccessful(session: OpticalScanSession): boolean {
    return session.state === 'completed' && session.result !== null;
}

const TERMINAL_STATES: ReadonlySet<string> = new Set([
    'completed',
    'timed-out',
    'disconnected',
    'cancelled',
    'worker-terminated',
    'failed',
    'expired',
]);
